/**
 * Client for the standalone invitation endpoints (`/v1/invitations/:token`).
 * Not org-scoped — the recipient may not belong to (or even know) the org
 * yet; the token identifies everything. Used by the InviteAccept page.
 */

const DEFAULT_BASE = 'http://localhost:3001';

export interface InvitePreview {
  id: string;
  email: string;
  orgRole: string;
  orgName: string;
  orgSlug: string;
}

export class InviteError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'InviteError';
    this.code = code;
  }
}

export function inviteApi(opts: { baseUrl?: string } = {}): {
  /** Preview an invitation without consuming it. null if invalid/expired. */
  preview(token: string): Promise<InvitePreview | null>;
  /** Accept as the currently signed-in user (email must match). */
  acceptAsMe(token: string): Promise<{ orgId: string }>;
} {
  const baseUrl = opts.baseUrl ?? import.meta.env.BLIKSYS_API_URL ?? DEFAULT_BASE;

  return {
    async preview(token) {
      const res = await fetch(`${baseUrl}/v1/invitations/${encodeURIComponent(token)}`, {
        credentials: 'include',
      });
      if (res.status === 404) return null;
      if (!res.ok)
        throw new InviteError('HTTP_ERROR', `Failed to load invitation (${res.status}).`);
      const body = (await res.json()) as { invitation: InvitePreview };
      return body.invitation;
    },
    async acceptAsMe(token) {
      const res = await fetch(
        `${baseUrl}/v1/invitations/${encodeURIComponent(token)}/accept-as-me`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
      );
      if (!res.ok) {
        let body: { code?: string; message?: string } = {};
        try {
          body = (await res.json()) as typeof body;
        } catch {
          /* non-JSON error */
        }
        throw new InviteError(
          body.code ?? 'HTTP_ERROR',
          body.message ?? `Could not accept the invitation (${res.status}).`,
        );
      }
      return (await res.json()) as { orgId: string };
    },
  };
}
